import WixProductEntry from "./pages/WixProductEntry";
import { useState, useEffect, useMemo, useRef } from "react";
import MetaTracking from './components/MetaTracking';
import { trackRegistration } from './utils/metaTracking';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation, Outlet, useNavigate } from "react-router-dom";
import {
  faWandMagicSparkles, faHeadset, faGear,
  faBookOpen, faGlobe, faFile, faCircleQuestion, faAddressCard,
  faBell, faChartSimple, faChartLine, faInbox, faUserTag, faFileLines, faRocket, faPalette, faPlug,
} from "@fortawesome/free-solid-svg-icons";
import AppHeader from "./components/AppHeader.js";
import AppSidebar from "./components/AppSidebar.js";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword.js";
import ResetPassword from "./pages/ResetPassword.js";
import UserSettings from "./pages/userSettings.js";
import Team from "./pages/Team.js";
import { OVERVIEW_NAV, isOverviewPath, isAccountPath, requiresAgent } from "./constants/overviewNav.js";
import McpAuthorize from "./pages/McpAuthorize";
import McpContinue from "./pages/McpContinue";
import McpProviderResult from "./pages/McpProviderResult";
import McpConnections from "./pages/McpConnections";
import { McpSessionRecovery } from './components/McpUi';
import BotsOverview from "./pages/bots.js";
import Overview from "./pages/Overview.js";
import ChannelsPage from "./pages/ChannelsPage.js";
import AppsPage from "./pages/AppsPage.js";
import { CLASSIC_PATH, STUDIO_PATH, useHomeView } from "./components/agentStudio/homeView.js";
import {
  SkillsPage, BehaviorPage, LeadGenerationPage, LiveSettingsPage, KnowledgePage, KnowledgeProviderPage,
  NotificationsPage, InsightsPage, InboxPage, LeadsPage, ReportingPage, DesignPage,
} from "./pages/sectionPages.js";
import WebflowRegister from "./pages/WebflowRegister.js";
import CheckoutSuccess from "./pages/CheckoutSuccess.js";
import WixAuthRedirect from "./pages/WixAuthRedirect.js";
import WixRegister from "./pages/WixRegister.js";
import WixOnboarding from "./pages/WixOnboarding.js";
import AgentCreation from "./pages/agent-creation/AgentCreation.js";
import WixEntryLoader from "./components/WixEntryLoader.js";
import WordpressRegister from "./pages/WordpressRegister.js";
import LandingPageRegister from "./pages/LandingPageRegister.js";
import WixConnect from "./pages/WixConnect.js";
import WebflowStepsModal from "./components/WebflowStepsModal.js";
import IntegrationModal from "./components/integrationModal.js";
import HelpPage from "./components/helpPage.js";
import PartnerProgram from "./pages/PartnerProgram.js";
import Feedback from "./components/feedback.js";
import FloatingChatWidget from "./components/floatingChatWidget.js";
import SourceQuestion from "./components/SourceQuestion.js";
import LinkedInBonus, { markBonusSignIn } from "./components/LinkedInBonus.js";
import WebflowPublishPending from "./components/WebflowPublishPending.js";
import LiveNotificationsProvider from "./components/LiveNotificationsProvider.js";
import { SelectedBotProvider, useSelectedBot } from "./contexts/SelectedBotContext.js";
import LeadCountBridge from "./components/LeadCountBridge.js";
import NoAgentsRedirect from "./components/NoAgentsRedirect.js";
import SingleAgentEntryGate from "./components/SingleAgentEntryGate.js";
import { markSingleAgentEntry } from "./utils/singleAgentEntry.js";
import Billing from "./pages/Billing.js";
import BotBilling from "./pages/BotBilling.js";
import ErrorModal from "./components/errorModal.js";
import LiveChat from "./pages/LiveChat.js";
import AcceptInvite from "./pages/AcceptInvite.js";
import PermissionToast from "./components/PermissionToast.js";
import { setRole as setStoredRole, clearRole as clearStoredRole, setCurrentOrgId } from "./utils/permissions";
import { fireTrialConversion } from "./utils/adsTracking";
import { pushSignupEvent } from "./utils/sourceTracking";
import {
  exchangeTelegramLogin,
  extractTelegramLogin,
  loginPathFor,
  redirectToLogin,
  refreshSessionIfNeeded,
  returnToFromSearch,
  takePendingReturnTo,
} from "./utils/session";
import { selectBotFromDeepLink } from "./utils/deepLinks";
import { isUrlTrainingHidden } from "./utils/webflowGate";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "./App.css";
import "./components/liveInbox.css";
import { useTranslation } from "react-i18next";
import "./translations.js";
import { UnsavedChangesProvider, useUnsavedChanges } from "./contexts/UnsavedChangesContext.js";
import useAppBindings from "./hooks/useAppBindings.js";

// ---------------------------------------------------------------------------
// Sidebar navigation model
//
// Single source of truth for the sidebar tree. Top-level entries with a
// `children` array are expandable groups that only toggle open (they do
// NOT navigate); the user must pick a leaf to land on a page. Top-level
// entries with a `path` (Overview, Notifications, Help) are leaves themselves.
// The order here defines `mainIndex`; the order of `children` defines
// `nestedIndex`. Both drive the highlight + the path lookup below.
// ---------------------------------------------------------------------------
// Ordered by the agent lifecycle — set up (Overview, Agent Studio, Knowledge
// Base) → launch (Deploy) → run (Live Chat, Activity, Notifications), with
// Help last as the standing exit to the help centre.
const NAV = [
  // The home: AI Studio (start page with the agent's figures and Mo). It replaces the
  // former Overview row and the former "AI Studio" group (Studio, Apps). Owners who
  // prefer the classic overview switch there and this row leads to it (navForBot).
  // The other Studio pages stay reachable by link and highlight this row: Apps now
  // lives in the "Your agent" panel; behaviour and lead capture were already authored
  // in the Studio.
  {
    labelKey: "nav_ai_studio",
    icon: faWandMagicSparkles,
    path: STUDIO_PATH,
    home: true,
    aliases: [CLASSIC_PATH, "/agent-studio/apps", "/agent-studio/behavior", "/agent-studio/lead-generation"],
  },
  {
    labelKey: "nav_knowledge_base",
    icon: faBookOpen,
    children: [
      { labelKey: "nav_website_links", icon: faGlobe, path: "/knowledge/website-links" },
      { labelKey: "nav_documents", icon: faFile, path: "/knowledge/documents" },
      { labelKey: "nav_faqs", icon: faCircleQuestion, path: "/knowledge/faqs" },
      { labelKey: "nav_business_context", icon: faAddressCard, path: "/knowledge/business-context" },
      // Platform apps that hold agent-readable data sit here, right before the
      // file connectors, because that is what they are to the owner: another
      // place the agent reads from. They only appear once the agent is actually
      // bound to the app (`requiresBinding`, resolved in navForBot) — an
      // unconnected leaf would lead to an empty page. Brand tokens
      // (keep-in-English glossary): rendered verbatim, like the connectors below.
      {
        label: "Wix",
        customIcon: true,
        customIconElement: (
          // Wordmark, not a glyph: constrain the HEIGHT and let the width follow,
          // or the three heavy letters get squashed into the square icon slot.
          // `nav-logo-mono` flips the black artwork to white on the purple
          // sidebar and restores the original once the row is selected (white
          // pill) — see AppSidebar.css.
          <img
            src="/wix-logo.png"
            alt=""
            className="nav-logo-mono"
            style={{ width: "auto", height: 11, objectFit: "contain" }}
          />
        ),
        path: "/knowledge/wix",
        requiresBinding: "wix",
      },
      {
        label: "Shopify",
        customIcon: true,
        customIconElement: (
          <img src="/shopify-logo.svg" alt="" style={{ width: 16, height: 16, objectFit: "contain" }} />
        ),
        path: "/knowledge/shopify",
        requiresBinding: "shopify",
      },
      {
        // Brand token (keep-in-English glossary): rendered verbatim.
        label: "Google Drive",
        customIcon: true,
        customIconElement: (
          <img src="/google-drive.png" alt="" style={{ width: 16, height: 16, objectFit: "contain" }} />
        ),
        path: "/knowledge/google-drive",
      },
      {
        // Brand token (keep-in-English glossary): rendered verbatim.
        label: "OneDrive",
        customIcon: true,
        customIconElement: (
          <img src="/onedrive.png" alt="" style={{ width: 18, height: 18, objectFit: "contain" }} />
        ),
        path: "/knowledge/onedrive",
      },
      {
        // Brand token (keep-in-English glossary): rendered verbatim.
        label: "Notion",
        customIcon: true,
        customIconElement: (
          <img src="/notion-logo.png" alt="" style={{ width: 16, height: 16, objectFit: "contain" }} />
        ),
        path: "/knowledge/notion",
      },
    ],
  },
  {
    labelKey: "nav_deploy",
    icon: faRocket,
    children: [
      { labelKey: "nav_channels", icon: faPlug, path: "/deploy/channels" },
      { labelKey: "nav_design", icon: faPalette, path: "/deploy/design" },
    ],
  },
  {
    labelKey: "nav_live_chat",
    icon: faHeadset,
    children: [
      {
        labelKey: "nav_live",
        customIcon: true,
        customIconElement: (
          <span className="live-radar-dot" style={{ width: 8, height: 8, margin: 0 }} />
        ),
        path: "/live",
      },
      { labelKey: "nav_settings", icon: faGear, path: "/live/settings" },
    ],
  },
  {
    labelKey: "nav_activity",
    icon: faChartSimple,
    children: [
      { labelKey: "nav_insights", icon: faChartLine, path: "/activity/insights" },
      { labelKey: "nav_inbox", icon: faInbox, path: "/activity/inbox" },
      { labelKey: "nav_leads", icon: faUserTag, path: "/activity/leads", key: "leads" },
      { labelKey: "nav_reporting", icon: faFileLines, path: "/activity/reporting" },
    ],
  },
  { labelKey: "nav_notifications", icon: faBell, path: "/notifications" },
  // Help is also reachable from the header's question-mark button and the
  // sidebar footer links, but neither is a place people look for a page —
  // give it a nav row of its own so it can't be missed. Same key + icon as
  // the header affordance so the three entry points read as one thing.
  { labelKey: "help", icon: faCircleQuestion, path: "/help" },
];

// Builds the sidebar model from NAV. Three filters:
//   1. Leaves marked `hidden: true` never render. They stay in NAV (and their
//      routes stay mounted) so deep links, legacy ?tab redirects and in-app
//      links keep working — they're just not offered in the sidebar.
//   2. Webflow Marketplace compliance: apps may not train from arbitrary URLs,
//      so Webflow-managed bots don't get the Website Links source at all.
//   3. Leaves marked `requiresBinding: '<app>'` only render when the selected
//      agent is bound to that app (Knowledge Base → Wix / Shopify). Their routes
//      stay mounted either way, so a deep link into an unbound agent still
//      resolves — the page then shows the connect prompt instead of data.
// The filtered model must feed BOTH the sidebar and findNavIndex — the
// active-row indicator positions by nested index, so the two must never
// disagree.
function navForBot(webflowManaged, appBindings, homeView = "studio") {
  // Account-level signal wins: a Webflow-connected account with no ready bot
  // yet must already be gated (that is the reviewer's very first screen).
  // Always false while the gate is disabled (utils/webflowGate.js).
  const webflowAccount = isUrlTrainingHidden(
    webflowManaged,
    localStorage.getItem("webflow_connected") === "1"
  );
  const bindings = appBindings || {};
  return NAV.map((group) =>
    group.home
      ? { ...group, path: homeView === "classic" ? CLASSIC_PATH : STUDIO_PATH }
      : group.children
      ? {
          ...group,
          children: group.children.filter(
            (leaf) =>
              !leaf.hidden &&
              !(webflowAccount && leaf.path === "/knowledge/website-links") &&
              !(leaf.requiresBinding && !bindings[leaf.requiresBinding])
          ),
        }
      : group
  );
}

// /overview: the classic overview for owners who chose it, otherwise the AI Studio
// start page. Waits for the stored choice only when this browser has none cached.
function HomeRoute() {
  const { view, ready } = useHomeView();
  if (!ready) return null;
  return view === "classic" ? <Overview /> : <Navigate to={STUDIO_PATH} replace />;
}

// Resolve a pathname to its sidebar position so the active row highlights.
// Returns { mainIndex: -1 } for non-sidebar routes (e.g. /my-account,
// /billing) so nothing is highlighted.
function findNavIndex(pathname, nav = NAV) {
  const overviewIndex = OVERVIEW_NAV.findIndex((e) => e.path === pathname);
  if (overviewIndex !== -1) return { mainIndex: overviewIndex, nestedIndex: null };
  for (let i = 0; i < nav.length; i++) {
    const group = nav[i];
    if (group.path && (pathname === group.path || (group.aliases || []).includes(pathname))) {
      return { mainIndex: i, nestedIndex: null };
    }
    if (group.children) {
      for (let j = 0; j < group.children.length; j++) {
        if (pathname === group.children[j].path) {
          return { mainIndex: i, nestedIndex: j };
        }
      }
    }
  }
  return { mainIndex: -1, nestedIndex: null };
}

// Legacy Bot-Settings tab name → new route. Used both by the /settings
// backward-compat redirect and by the `bot-settings-set-tab` bridge so
// in-page "jump to X" buttons keep working after the tab shell is gone.
const SETTINGS_TAB_PATHS = {
  knowledge: "/knowledge/website-links",
  behavior: "/agent-studio/behavior",
  design: "/deploy/design",
  live_chat: "/live/settings",
  notifications: "/notifications",
  skills: "/agent-studio/skills",
  leads: "/agent-studio/lead-generation", // Warm-lead settings moved to the Lead Generation skill.
};

function LocationWatcher({ setIntegrationBotId, setOpenIntegrationModal, i18n, setSelectedIndex, nav = NAV }) {
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const botIdParam = params.get("bot_id");
    const showIntegrationModal = params.get("showIntegrationModal");

    setIntegrationBotId(botIdParam || null);
    setOpenIntegrationModal(!!showIntegrationModal);

    const urlLangParam = Array.from(params.entries()).find(([key]) =>
      ["en", "de", "fr"].includes(key)
    );
    if (urlLangParam) {
      const lang = urlLangParam[0];
      i18n.changeLanguage(lang);
      localStorage.setItem("language", lang);
    }
  }, [location.search, setIntegrationBotId, setOpenIntegrationModal, i18n]);

  useEffect(() => {
    setSelectedIndex(findNavIndex(location.pathname, nav));
  }, [location.pathname, setSelectedIndex, nav]);

  return null;
}

// Lifts the selected bot's webflow_connected flag out of the
// SelectedBotProvider (mounted inside AppLayout) up to AppShell, which
// builds the nav model — same pattern as LeadCountBridge.
function WebflowNavBridge({ setFlag }) {
  const { selectedBot } = useSelectedBot();
  useEffect(() => {
    // Either reason gates the nav: the account is Webflow-connected, or this
    // agent was declared Webflow-hosted when it was created.
    setFlag(!!selectedBot?.webflow_connected || !!selectedBot?.webflow_hosted);
  }, [selectedBot, setFlag]);
  return null;
}

// Lifts the selected agent's platform-app bindings (Wix / Shopify) out of the
// SelectedBotProvider up to AppShell, which builds the nav model — same pattern
// as WebflowNavBridge. The hook caches per agent, so the provider pages read the
// same answer without a second request.
function AppBindingsNavBridge({ setBindings }) {
  const { selectedBotId } = useSelectedBot();
  const bindings = useAppBindings(selectedBotId);
  useEffect(() => { setBindings(bindings); }, [bindings, setBindings]);
  return null;
}

// Route guard: the Website Links section doesn't exist for Webflow-managed
// bots (see navForBot) — deep links land on Documents instead.
function WebsiteLinksRoute() {
  const { selectedBot } = useSelectedBot();
  // Always false while the gate is disabled (utils/webflowGate.js).
  const webflowAccount = isUrlTrainingHidden(
    localStorage.getItem("webflow_connected") === "1",
    selectedBot?.webflow_connected,
    selectedBot?.webflow_hosted
  );
  if (webflowAccount) {
    return <Navigate to="/knowledge/documents" replace />;
  }
  return <KnowledgePage section="links" />;
}

// Bridges the legacy `bot-settings-set-tab` event (still dispatched by a
// few in-page "jump to X settings" buttons, e.g. inside Live Chat
// settings) onto the new standalone routes, now that the tabbed Bot
// Settings shell is gone.
function SettingsTabBridge() {
  const navigate = useNavigate();
  const { requestUnsavedAction } = useUnsavedChanges();

  useEffect(() => {
    const handler = (e) => {
      const tab = e?.detail?.tab;
      const path = tab && SETTINGS_TAB_PATHS[tab];
      if (path) requestUnsavedAction(() => navigate(path));
    };
    window.addEventListener("bot-settings-set-tab", handler);
    return () => window.removeEventListener("bot-settings-set-tab", handler);
  }, [navigate, requestUnsavedAction]);

  return null;
}

// Unauthenticated hit on a protected route: send to /login carrying the
// requested page as ?returnTo= (handleLogin honours it), so a deep link — a
// notification's "Open live chat", an invite — survives the login instead
// of dumping the user on My Bots.
function LoginRedirect() {
  const location = useLocation();
  return <Navigate to={loginPathFor(location)} replace />;
}

// /settings?botId=&tab=  →  the matching new section route.
function SettingsRedirect() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const botId = params.get("botId") || params.get("bot_id");
  const tab = params.get("tab");
  useEffect(() => {
    selectBotFromDeepLink(botId);
  }, [botId]);
  const path = (tab && SETTINGS_TAB_PATHS[tab]) || "/agent-studio/skills";
  return <Navigate to={path} replace />;
}

// /analytics?tab=  →  /activity/<tab>.
function AnalyticsRedirect() {
  const location = useLocation();
  const tab = new URLSearchParams(location.search).get("tab");
  const valid = ["insights", "inbox", "leads", "reporting"];
  const dest = valid.includes(tab) ? `/activity/${tab}` : "/activity/insights";
  return <Navigate to={dest} replace />;
}

// /integration?bot_id=  →  /deploy/channels (preserving query + state so
// the Webflow "activated" flow and bot deep-links keep working).
function IntegrationRedirect() {
  const location = useLocation();
  const botId =
    new URLSearchParams(location.search).get("bot_id") ||
    new URLSearchParams(location.search).get("botId");
  useEffect(() => {
    selectBotFromDeepLink(botId);
  }, [botId]);
  return (
    <Navigate
      to={{ pathname: "/deploy/channels", search: location.search }}
      state={location.state}
      replace
    />
  );
}

function AppLayout({
  isWixSite,
  wixSite,
  t,
  pages,
  isMobile,
  showBackToBots,
  leadsCount,
  setUnreadLeadsCount,
  setWebflowManaged,
  setAppBindings,
  selectedIndex,
  setSelectedIndex,
  sidebarVisible,
  toggleSidebar,
  webflowHelpModalOpen,
  setWebflowHelpModalOpen,
  openIntegrationModal,
  setOpenIntegrationModal,
  integrationBotId,
  logout,
}) {
  return (
    <LiveNotificationsProvider>
    <SelectedBotProvider>
      <LeadCountBridge setCount={setUnreadLeadsCount} />
      <WebflowNavBridge setFlag={setWebflowManaged} />
      <AppBindingsNavBridge setBindings={setAppBindings} />
      <NoAgentsRedirect />
      <SingleAgentEntryGate isWixSite={isWixSite}>
      <div className="app-shell">
        <AppHeader
          onToggleSidebar={toggleSidebar}
          onLogout={logout}
          showLogout={!isWixSite}
          wixSite={isWixSite ? wixSite : null}
          isMobile={isMobile}
        />

        <AppSidebar
          pages={pages}
          showBackToBots={showBackToBots}
          leadsCount={leadsCount}
          selectedIndex={selectedIndex}
          setSelectedIndex={setSelectedIndex}
          t={t}
          visible={sidebarVisible}
          isMobile={isMobile}
        />

        {/* Backdrop behind the slide-over sidebar on mobile. */}
        <div
          className={`app-sidebar-backdrop${sidebarVisible ? " show" : ""}`}
          onClick={toggleSidebar}
        />

        <main className="app-content">
          <Outlet />
        </main>

        <WebflowStepsModal
          open={webflowHelpModalOpen}
          onFinish={() => setWebflowHelpModalOpen(false)}
        />
        <IntegrationModal
          open={openIntegrationModal}
          onClose={() => setOpenIntegrationModal(false)}
          botId={integrationBotId}
        />
        <FloatingChatWidget />
        <SourceQuestion />
        <LinkedInBonus />
        <WebflowPublishPending isWixSite={isWixSite} />
        <SettingsTabBridge />
      </div>
      </SingleAgentEntryGate>
    </SelectedBotProvider>
    </LiveNotificationsProvider>
  );
}

function AppShell() {
  const [selectedIndex, setSelectedIndex] = useState({
    mainIndex: 0,
    nestedIndex: 0,
  });
  const [sidebarVisible, setSidebarVisible] = useState(window.innerWidth > 768);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [webflowHelpModalOpen, setWebflowHelpModalOpen] = useState(false);
  const [isWixSite, setIsWixSite] = useState(false);
  // The Wix install the dashboard was opened from: the account menu's "Uninstall app" links to its Manage Apps page.
  const [wixSite, setWixSite] = useState(null);
  const [openIntegrationModal, setOpenIntegrationModal] = useState(false);
  const [integrationBotId, setIntegrationBotId] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [mcpSessionError, setMcpSessionError] = useState(false);
  const [mcpSessionAttempt, setMcpSessionAttempt] = useState(0);
  const [unreadLeadsCount, setUnreadLeadsCount] = useState(0);
  // Selected bot is Webflow-managed → Website Links leaves the nav model
  // (lifted out of the provider by WebflowNavBridge).
  const [webflowManaged, setWebflowManaged] = useState(false);
  // Selected agent's platform-app bindings → the Knowledge Base Wix / Shopify
  // leaves (lifted out of the provider by AppBindingsNavBridge).
  const [appBindings, setAppBindings] = useState(null);
  // Bumped once the Google sign-in code was traded for the session: the
  // sign-in effect runs again with the handed-over token.
  const [signinHandoff, setSigninHandoff] = useState(0);
  const navigate = useNavigate();
  const { requestUnsavedAction } = useUnsavedChanges();
  const runNavigation = (action) => {
    requestUnsavedAction(action);
  };

  useEffect(() => {
    // public/index.html moves a ?token= out of the address bar before any
    // script loads and hands the query over in memory; read it once.
    const handedParams = window.__UB_URL_PARAMS__;
    if (handedParams !== undefined) {
      try { delete window.__UB_URL_PARAMS__; } catch { window.__UB_URL_PARAMS__ = undefined; }
    }
    const params = new URLSearchParams(handedParams || window.location.search);
    // Google sign-in lands with a one-time code (never the session token in a
    // URL); trade it for the session, then continue like a token sign-in.
    const signinCode = params.get("signin");
    if (signinCode && !params.get("token")) {
      (async () => {
        try {
          const r = await fetch(`${process.env.REACT_APP_API_BASE_URL}auth/handoff`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ code: signinCode }),
          });
          const d = r.ok ? await r.json() : null;
          if (d && d.token && d.user_id) {
            const next = new URLSearchParams({ token: d.token, user_id: String(d.user_id), first_name: d.first_name || "" });
            if (params.get("just_registered") === "1") next.set("just_registered", "1");
            window.__UB_URL_PARAMS__ = `?${next.toString()}`;
            setSigninHandoff((n) => n + 1);
            return;
          }
        } catch { /* expired or offline: the login page */ }
        setLoading(false);
        navigate("/login", { replace: true });
      })();
      return;
    }
    const token = params.get("token");
    const user_id = params.get("user_id");
    const first_name = params.get("first_name");
    const instance = params.get("instance");
    // NOTE: the old ?wp_token= sign-in path is gone for good — it POSTed to
    // auth/wp/login_with_token, a backend route that never existed. The
    // WordPress entries are /wordpress-register?connect=<code> (plugin >= 1.1.0),
    // the legacy query-param form, and ?is_initial_wp_setup=1.
    const wpInitialSetup = params.get("is_initial_wp_setup");

    const urlLangParam = Array.from(params.entries()).find(([key]) =>
      ["en", "de", "fr"].includes(key)
    );
    if (urlLangParam) {
      const lang = urlLangParam[0];
      i18n.changeLanguage(lang);
      localStorage.setItem("language", lang);
    }

    // The SINGLE Wix-entry decision. One call to /resolve returns one "action" and we
    // route on it. There is deliberately NO branch that renders the portal /login —
    // every outcome (REGISTER / ONBOARDING / DASHBOARD / ERROR / network failure) lands
    // inside the Wix /wix-start flow. This replaces the old login_with_instance +
    // continue_installation split that caused the random behaviour.
    async function resolveWixInstance(instance) {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}auth/wix/resolve?instance=${encodeURIComponent(instance)}`
        );
        let data = {};
        try { data = await res.json(); } catch (e) { data = {}; }
        const action = data.action;

        if (action === "DASHBOARD") {
          // Finished agent for this site → log in and open the portal dashboard.
          localStorage.setItem("token", data.token);
          localStorage.setItem("user_id", data.user_id);
          localStorage.setItem("first_name", data.first_name || "");
          localStorage.setItem("date_format", data.date_format || "YYYY-MM-DD");
          localStorage.setItem("timezone", data.timezone || "UTC");
          // open the agent of this site, not a selection left in this storage by an earlier session
          selectBotFromDeepLink(data.bot_id);
          setWixSite({ instanceId: data.wix_instance_id || "", siteId: data.site_id || "" });
          setIsWixSite(true);
          setIsAuthenticated(true);
          // The usual Wix account owns exactly one agent — SingleAgentEntryGate
          // then forwards /my-bots to /overview so the dashboard opens on it.
          markSingleAgentEntry();
          navigate("/my-bots", { replace: true });
          return;
        }

        if (action === "ONBOARDING") {
          // Account exists, this site has no finished agent → resume the Wix onboarding.
          // The token is stored so the onboarding's API calls are authenticated; the
          // step + agent are read from the DB (/onboarding/state) on mount, not the URL.
          localStorage.setItem("token", data.token);
          localStorage.setItem("user_id", data.user_id);
          localStorage.setItem("first_name", data.first_name || "");
          navigate(`/wix-start?wix_instance_id=${encodeURIComponent(data.wix_instance_id || "")}`, { replace: true });
          return;
        }

        if (action === "REGISTER") {
          // No account for this site yet → onboarding account-creation step. Clear any
          // stale token so nothing authenticates as a previous owner.
          localStorage.removeItem("token");
          localStorage.removeItem("user_id");
          localStorage.removeItem("first_name");
          const q = new URLSearchParams({ wix_instance_id: data.wix_instance_id || "", register: "1" });
          if (data.user_email) q.set("email", data.user_email);
          navigate(`/wix-start?${q.toString()}`, { replace: true });
          return;
        }

        // action === "ERROR" (not_installed / invalid_token / server_error) OR any
        // unexpected shape → a Wix-only error screen. NEVER the portal login.
        navigate(`/wix-start?error=${encodeURIComponent(data.reason || "resolve_failed")}`, { replace: true });
      } catch (err) {
        // Network / total failure → still a Wix screen, never the portal login.
        console.error("Wix resolve failed:", err);
        navigate(`/wix-start?error=resolve_failed`, { replace: true });
      } finally {
        setLoading(false);
      }
    }

    if (window.location.pathname.startsWith("/wix/")) {
      setLoading(false);
    } else if (instance) {
      resolveWixInstance(instance);
    } else if (wpInitialSetup) {
      const search = window.location.search;
      window.history.pushState({}, "", "/wordpress-register" + search);
      window.dispatchEvent(new PopStateEvent("popstate"));
      setLoading(false);
    } else if (token && user_id) {
      localStorage.setItem("token", token);
      localStorage.setItem("user_id", user_id);
      localStorage.setItem("first_name", first_name || "");
      // Google Ads signup conversion for the OAuth return leg: the backend
      // appends just_registered=1 ONLY when the Google callback actually
      // created a new account (plain logins land here without it). Latched
      // in localStorage inside fireTrialConversion, so a back-navigation to
      // this URL cannot double-fire.
      if (params.get("just_registered") === "1") {
        fireTrialConversion();
        trackRegistration(user_id, "direct");
        pushSignupEvent("google");
      }
      // Embedded handoffs (Shopify admin "Manage your agent") append bot_id so
      // the global selector opens on the agent the user came from; no param =
      // unchanged reconcile-to-stored/first behaviour.
      selectBotFromDeepLink(params.get("bot_id") || params.get("botId"));
      setIsAuthenticated(true);
      markBonusSignIn(); // a sign-in: the LinkedIn bonus may show once
      // Google OAuth leaves the SPA, so Login.js parks the ?returnTo= it was
      // given in sessionStorage; land there (e.g. the live conversation a
      // notification linked to) instead of the generic overview.
      navigate(takePendingReturnTo() || "/overview", { replace: true });
      setLoading(false);
    } else {
      setLoading(false);
    }
  }, [i18n, navigate, signinHandoff]);

  // Sliding session: once the stored JWT is past half its lifetime, silently
  // re-mint it (POST /api/auth/refresh). Checked on mount, every 5 minutes,
  // and whenever the tab becomes visible again — a live-chat operator who
  // keeps the portal open for days therefore never hits the hard expiry.
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    let cancelled = false;
    const tick = () => { if (!cancelled) refreshSessionIfNeeded(); };
    tick();
    const interval = setInterval(tick, 5 * 60 * 1000);
    const onVisible = () => { if (document.visibilityState === "visible") tick(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [isAuthenticated]);

  useEffect(() => {
    // Mobile browsers fire `resize` constantly WITHOUT the width changing
    // (on-screen keyboard, URL-bar collapse/expand — only the height moves).
    // Naively deriving sidebar visibility from every resize event force-closed
    // the just-opened mobile sidebar within one frame, so the burger appeared
    // dead. Only touch the sidebar when the width actually crosses the
    // desktop/mobile breakpoint.
    let lastWidth = window.innerWidth;
    const handleResize = () => {
      const width = window.innerWidth;
      if (width === lastWidth) return;
      const wasDesktop = lastWidth > 768;
      const isDesktop = width > 768;
      lastWidth = width;
      setIsMobile(!isDesktop);
      if (wasDesktop !== isDesktop) {
        setSidebarVisible(isDesktop);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (window.innerWidth <= 768) {
      setSidebarVisible(false);
    }
  }, [selectedIndex]);

  useEffect(() => {
    let alive = true;

    // Skip token validation if a Wix/WP login or onboarding flow is active. Those
    // pages manage their own auth and would otherwise validate a stale token (e.g.
    // one left in localStorage after the account was deleted) and bounce the user to
    // /login instead of showing the registration form. This must cover not just the
    // entry params but the onboarding ROUTES themselves, because the Wix flow redirects
    // to /wix-start?wix_instance_id=... where the original ?instance= param is gone.
    const params = new URLSearchParams(window.location.search);
    const path = window.location.pathname;
    // Only the self-managed registration / onboarding flows (NOT /wix-connect or
    // /wix-auth-redirect, which act on an already-authenticated account).
    // /webflow-register belongs here too: validating an existing session there
    // flipped isAuthenticated, re-ran this effect and remounted the callback
    // behind the spinner; the remount had no ?code left and sent the customer
    // back to Webflow OAuth (install loop, Webflow Marketplace #1181064).
    const onboardingRoute = ["/wix/", "/wix-register", "/wix-start", "/wordpress-register", "/webflow-register"].some((r) => path.startsWith(r));
    if (params.get("instance") || params.get("wix_instance_id") || onboardingRoute) {
      return;
    }

    (async () => {
      setLoading(true);

      // Telegram login button: the notification deep link arrives with the
      // tapping user's Telegram identity + signature appended by Telegram
      // (id, auth_date, hash, ...). Strip those fields from the URL FIRST
      // (they are single-use and must never be re-submitted on a re-render
      // or refresh), keep our own bot/session params, then exchange them for
      // a session. Any refusal (stale, replayed, no linked account) falls
      // through to the normal path: an existing session keeps working, or
      // the guard sends the user to /login?returnTo=<this deep link>.
      const { telegram, search: cleanSearch } = extractTelegramLogin(window.location.search);
      if (telegram) {
        const cleanUrl = `${window.location.pathname}${cleanSearch}${window.location.hash || ""}`;
        window.history.replaceState(window.history.state, "", cleanUrl);
        const botId = new URLSearchParams(cleanSearch).get("bot");
        const data = await exchangeTelegramLogin({ telegram, botId });
        if (data && data.token) {
          // Same persistence as handleLogin, minus the navigation: we are
          // already on the destination page.
          localStorage.setItem("token", data.token);
          localStorage.setItem("user_id", data.user_id);
          localStorage.setItem("first_name", data.first_name || "");
          if (data.role) setStoredRole(data.role, data.owner_user_id);
          if (data.current_org_id !== undefined && data.current_org_id !== null) {
            setCurrentOrgId(data.current_org_id);
          }
          selectBotFromDeepLink(botId);
        }
      }

      const token = localStorage.getItem("token");

      if (!token) {
        if (alive) {
          setIsAuthenticated(false);
          setLoading(false);
        }
        return;
      }

      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}auth/validate_token`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (!res.ok) {
          const failure = new Error("Session validation failed");
          failure.status = res.status;
          throw failure;
        }

        const user = await res.json();
        localStorage.setItem("user_id", user.user_id);
        localStorage.setItem("first_name", user.first_name);
        localStorage.setItem("language", user.language);
        localStorage.setItem("date_format", user.date_format);
        localStorage.setItem("timezone", user.timezone || "UTC");
        // Account-level Webflow signal (Marketplace compliance): gates every
        // arbitrary-URL training surface, including for accounts that have no
        // (ready) bot yet — a per-bot flag cannot cover that state.
        localStorage.setItem("webflow_connected", user.webflow_connected ? "1" : "0");
        // Webflow now or before (token, Webflow agent or site): new agents get
        // no website URL field, also after a Disconnect (#1172876 F2).
        localStorage.setItem("webflow_history", user.webflow_history ? "1" : "0");
        if (user.role) setStoredRole(user.role, user.owner_user_id);
        if (user.current_org_id !== undefined && user.current_org_id !== null) {
          setCurrentOrgId(user.current_org_id);
        }
        i18n.changeLanguage(user.language);
        if (alive) setIsAuthenticated(true);
        // The unread-leads count is fetched per selected bot by
        // LeadCountBridge (inside SelectedBotProvider), not here.
      } catch (e) {
        console.error("Error validating token:", e);
        if (alive) {
          if (['/mcp/authorize', '/mcp-connections'].includes(window.location.pathname)) {
            if ([401, 403].includes(e.status)) redirectToLogin({ clearRole: clearStoredRole });
            else setMcpSessionError(true);
          } else logout();
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [i18n, isAuthenticated, mcpSessionAttempt]);

  const handleLogin = (authStatus, token, user_id, first_name, language, date_format, timezone, role, owner_user_id, current_org_id) => {
    if (authStatus) {
      localStorage.setItem("token", token);
      localStorage.setItem("user_id", user_id);
      localStorage.setItem("first_name", first_name);
      localStorage.setItem("language", language);
      localStorage.setItem("date_format", date_format);
      localStorage.setItem("timezone", timezone || "UTC");
      if (role) setStoredRole(role, owner_user_id);
      if (current_org_id !== undefined && current_org_id !== null) {
        setCurrentOrgId(current_org_id);
      }
      i18n.changeLanguage(language);
      setIsAuthenticated(true);
      markBonusSignIn(); // a sign-in: the LinkedIn bonus may show once
      // Honor ?returnTo=... so e.g. the accept-invite page or a notification
      // deep link (/live?bot=..&session=..) can send the user through login
      // and land back on itself. Validated by sanitizeReturnTo (same-origin
      // path only, never an auth page) so it can't become an open redirect.
      const returnTo = returnToFromSearch(window.location.search) || "/my-bots";
      // Only the DEFAULT landing gets the single-agent skip (one agent →
      // SingleAgentEntryGate forwards /my-bots to /overview). An explicit
      // ?returnTo= is the caller's destination and must be honoured as-is.
      if (returnTo === "/my-bots") markSingleAgentEntry();
      navigate(returnTo, { replace: true });
    }
  };

  const handleRegister = (authStatus, token, user_id, first_name) => {
    if (authStatus) {
      localStorage.setItem("token", token);
      localStorage.setItem("user_id", user_id);
      localStorage.setItem("first_name", first_name);
      localStorage.setItem("date_format", 'YYYY-MM-DD');
      localStorage.setItem("timezone", "UTC");
      setIsAuthenticated(true);
      markBonusSignIn(); // shows once the new account has an agent (the backend decides)
      // Fresh sign-up has no agent yet — land on the Agents overview (matching
      // handleLogin) so the sidebar shows Agents / Workspaces / Team members
      // rather than the agent-level nav for an agent that doesn't exist. A
      // ?returnTo= is honoured when that page works without an agent (e.g. the
      // Partner program from the website), and for the Wix / MCP flows.
      const target = returnToFromSearch(window.location.search);
      const agentFree = target && !requiresAgent(target.split(/[?#]/)[0]);
      navigate(target?.startsWith("/wix/") || target === '/mcp/authorize' || agentFree ? target : "/my-bots", { replace: true });
    }
  };

  const handleWebflowRegister = (authStatus, token, user_id) => {
    if (authStatus) {
      localStorage.setItem("token", token);
      localStorage.setItem("user_id", user_id);
      localStorage.setItem("date_format", 'YYYY-MM-DD');
      localStorage.setItem("timezone", "UTC");
      setIsAuthenticated(true);
      // The WebflowStepsModal stays mounted but is no longer opened on arrival:
      // its three steps still describe the pre-IA sidebar (All Bots / Settings /
      // Integration). Set webflowHelpModalOpen to bring it back.
      navigate("/overview", { replace: true });
    }
  };

  // "Connect Webflow" started from the dashboard returns through the same
  // callback. The token validation is skipped on /webflow-register, so the
  // already signed-in customer is marked authenticated here, right before the
  // protected destination renders (the validation then runs there).
  const handleWebflowConnected = (to, state) => {
    setIsAuthenticated(true);
    navigate(to, state ? { replace: true, state } : { replace: true });
  };

  const handleWixCompleteSetup = (token, user_id) => {
    localStorage.setItem("token", token);
    localStorage.setItem("user_id", user_id);
    localStorage.setItem("date_format", 'YYYY-MM-DD');
    localStorage.setItem("timezone", "UTC");
    setIsAuthenticated(true);
    navigate("/overview", { replace: true });
  };

  const handleLandingCompleteSetup = (token, user_id) => {
    localStorage.setItem("token", token);
    localStorage.setItem("user_id", user_id);
    localStorage.setItem("language", "en");
    localStorage.setItem("first_name", "User");
    localStorage.setItem("date_format", 'YYYY-MM-DD');
    localStorage.setItem("timezone", "UTC");
    setIsAuthenticated(true);
    navigate("/overview", { replace: true });
  };

  const handleLanguageChangeFromAdmin = (language, date_format, timezone) => {
    localStorage.setItem("language", language);
    i18n.changeLanguage(language);
    localStorage.setItem("date_format", date_format);
    localStorage.setItem("timezone", timezone);
  };

  // Selects a bot in the global SelectedBotContext (which lives inside
  // AppLayout, below this component) via a window event, then navigates.
  const selectBotGlobally = (botId) => {
    if (botId == null) return;
    localStorage.setItem("selectedBotId", botId);
    localStorage.setItem("selectedBotIds", JSON.stringify([botId]));
    window.dispatchEvent(
      new CustomEvent("ub:select-bot", { detail: { botId } })
    );
  };

  const handelAddBotClick = () => {
    runNavigation(() => {
      // Flag for BotsOverview to auto-open AddBotModal post navigation
      localStorage.setItem('triggerAddBotModal', '1');
      navigate("/my-bots");
      if (window.innerWidth <= 768) {
        setSidebarVisible(false);
      }
    });
  };

  const handleSettingsClick = (bot, tab = null) => {
    runNavigation(() => {
      selectBotGlobally(bot.id);
      const path = (tab && SETTINGS_TAB_PATHS[tab]) || "/agent-studio/skills";
      navigate(path);
      if (window.innerWidth <= 768) {
        setSidebarVisible(false);
      }
    });
  };

  const handleAnalyticsClick = (bot) => {
    runNavigation(() => {
      selectBotGlobally(bot.id);
      navigate("/activity/insights");
      if (window.innerWidth <= 768) {
        setSidebarVisible(false);
      }
    });
  };

  function logout() {
    setSelectedIndex({ mainIndex: 0, nestedIndex: 0 });
    setIsAuthenticated(false);
    localStorage.removeItem("token");
    localStorage.removeItem("user_id");
    localStorage.removeItem("selectedBotId");
    localStorage.removeItem("selectedBotIds");
    // Never leak one account's Webflow state into the next session.
    localStorage.removeItem("webflow_connected");
    localStorage.removeItem("webflow_history");
    clearStoredRole();
    navigate("/login", { replace: true });
  }

  // Render the sidebar from the static NAV model. The unread-leads badge is
  // drawn by AppSidebar itself (it knows the active row, so it can colour the
  // badge correctly on the selected white pill).
  const location = useLocation();
  // Account pages (Account settings / Billing) have no nav level of their own:
  // keep the sidebar on whichever level the user came from instead of flipping
  // to the agent nav. We cache the last level decided on a real page and reuse
  // it while on an account page — mirrors the header's selector logic
  // (AppHeader.js) so the two surfaces stay in agreement.
  const isAccountPage = isAccountPath(location.pathname);
  const isOverviewRaw = isOverviewPath(location.pathname);
  const lastOverview = useRef(isOverviewRaw);
  if (!isAccountPage) {
    lastOverview.current = isOverviewRaw;
  }
  const isOverview = isAccountPage ? lastOverview.current : isOverviewRaw;

  // Memoized for a stable identity: navModel feeds LocationWatcher's effect
  // deps. navForBot(true) builds a fresh array, so without the memo every
  // AppShell render re-fired setSelectedIndex (new object) -> re-render ->
  // an endless effect loop that also kept closing the mobile sidebar.
  // Depend on the two flags, not the bindings object: the hook hands back a new
  // object on every refetch, which would rebuild the model (and re-fire
  // LocationWatcher) even when nothing about the nav actually changed.
  const wixBound = !!appBindings?.wix;
  const shopifyBound = !!appBindings?.shopify;
  const { view: homeView } = useHomeView();
  const navModel = useMemo(
    () => navForBot(webflowManaged, { wix: wixBound, shopify: shopifyBound }, homeView),
    [webflowManaged, wixBound, shopifyBound, homeView],
  );
  const pages = isOverview
    ? OVERVIEW_NAV.map((e) => ({
        label: e.label,
        icon: e.icon,
        path: e.path,
        nestedItems: null,
      }))
    : navModel.map((group) => ({
        // Resolve the NAV label here (render site) — the NAV model stores a
        // `labelKey` for translatable rows, or a plain `label` for brand
        // tokens (Google Drive / OneDrive / Notion) that stay verbatim.
        label: group.labelKey ? t(group.labelKey) : group.label,
        path: group.path,
        icon: group.icon,
        nestedItems: group.children
          ? group.children.map((leaf) => ({
              ...leaf,
              label: leaf.labelKey ? t(leaf.labelKey) : leaf.label,
            }))
          : group.children,
      }));

  if (mcpSessionError && ['/mcp/authorize', '/mcp-connections'].includes(window.location.pathname)) {
    return <McpSessionRecovery onRetry={() => { setLoading(true); setMcpSessionError(false); setMcpSessionAttempt(value => value + 1); }} />;
  }

  if (loading) {
    // On the Wix entry (the embedded ?instance= session, or once we're on /wix-start),
    // show the SAME purple loader the onboarding uses, so there's one continuous loader
    // instead of the white portal spinner flickering into the purple one.
    const wixEntry =
      new URLSearchParams(window.location.search).get("instance") ||
      window.location.pathname.startsWith("/wix-start");
    if (wixEntry) return <WixEntryLoader />;
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <>
      <LocationWatcher
        setIntegrationBotId={setIntegrationBotId}
        setOpenIntegrationModal={setOpenIntegrationModal}
        i18n={i18n}
        setSelectedIndex={setSelectedIndex}
        nav={navModel}
      />

      <div className="app-container">
        <Routes>
          <Route
            path="/login"
            element={
              isAuthenticated ? (
                <Navigate to="/overview" replace />
              ) : (
                <Login onLogin={handleLogin} />
              )
            }
          />
          <Route
            path="/register"
            element={
              isAuthenticated ? (
                <Navigate to="/overview" replace />
              ) : (
                <Register onRegister={handleRegister} />
              )
            }
          />
          <Route path="/webflow-register" element={<WebflowRegister onSetupComplete={handleWebflowRegister} onConnected={handleWebflowConnected} />} />
          <Route path="/checkout-success" element={<CheckoutSuccess />} />
          <Route path="/wix-auth-redirect" element={<WixAuthRedirect />} />
          <Route path="/wix-connect" element={<WixConnect />} />
          <Route path="/wix-register" element={<WixRegister />} />
          <Route path="/wix-start" element={<WixOnboarding />} />
          <Route path="/wix/:appKey/:phase" element={<WixProductEntry />} />
          {/* Create-agent flow (authenticated; full-screen, outside AppLayout). */}
          <Route path="/agents/new" element={isAuthenticated ? <AgentCreation /> : <LoginRedirect />} />
          <Route path="/mcp/authorize" element={isAuthenticated ? <McpAuthorize /> : <LoginRedirect />} />
          <Route path="/mcp/continue" element={isAuthenticated ? <McpContinue /> : <LoginRedirect />} />
          <Route path="/mcp/provider-result" element={<McpProviderResult />} />
          {/* Security controls must remain usable when agent/live services fail. */}
          <Route path="/mcp-connections" element={isAuthenticated ? <McpConnections /> : <LoginRedirect />} />
          <Route path="/agents/new/:botId" element={isAuthenticated ? <AgentCreation /> : <LoginRedirect />} />
          <Route path="/agents/new/:botId/:step" element={isAuthenticated ? <AgentCreation /> : <LoginRedirect />} />
          <Route path="/wordpress-register" element={<WordpressRegister onSetupComplete={handleWixCompleteSetup} />} />
          <Route path="/create-chatbot" element={<LandingPageRegister onSetupComplete={handleLandingCompleteSetup} />} />
          <Route path="/accept-invite/:token" element={<AcceptInvite />} />
          <Route
            path="/forgot-password"
            element={
              isAuthenticated ? <Navigate to="/overview" replace /> : <ForgotPassword />
            }
          />
          <Route
            path="/reset-password"
            element={
              isAuthenticated ? <Navigate to="/overview" replace /> : <ResetPassword />
            }
          />

          <Route
            path="/"
            element={
              isAuthenticated ? (
                <AppLayout
                  isWixSite={isWixSite}
                  wixSite={wixSite}
                  t={t}
                  pages={pages}
                  isMobile={isMobile}
                  showBackToBots={!isOverview}
                  leadsCount={unreadLeadsCount}
                  setUnreadLeadsCount={setUnreadLeadsCount}
                  setWebflowManaged={setWebflowManaged}
                  setAppBindings={setAppBindings}
                  selectedIndex={selectedIndex}
                  setSelectedIndex={setSelectedIndex}
                  sidebarVisible={sidebarVisible}
                  toggleSidebar={() => setSidebarVisible(!sidebarVisible)}
                  webflowHelpModalOpen={webflowHelpModalOpen}
                  setWebflowHelpModalOpen={setWebflowHelpModalOpen}
                  openIntegrationModal={openIntegrationModal}
                  setOpenIntegrationModal={setOpenIntegrationModal}
                  integrationBotId={integrationBotId}
                  logout={logout}
                />
              ) : (
                <LoginRedirect />
              )
            }
          >
            <Route index element={<Navigate to="overview" replace />} />

            <Route path="overview" element={<HomeRoute />} />

            {/* Agent Studio */}
            <Route path="agent-studio/skills" element={<SkillsPage />} />
            <Route path="agent-studio/behavior" element={<BehaviorPage />} />
            <Route path="agent-studio/lead-generation" element={<LeadGenerationPage />} />
            <Route path="agent-studio/apps" element={<AppsPage />} />

            {/* Live */}
            <Route path="live" element={<LiveChat />} />
            <Route path="live/settings" element={<LiveSettingsPage />} />

            {/* Knowledge Base — one fixed section per sidebar leaf */}
            <Route path="knowledge/website-links" element={<WebsiteLinksRoute />} />
            <Route path="knowledge/documents" element={<KnowledgePage section="documents" />} />
            <Route path="knowledge/faqs" element={<KnowledgePage section="faq" />} />
            <Route path="knowledge/business-context" element={<KnowledgePage section="business_context" />} />
            {/* Platform apps. Mounted unconditionally so a deep link resolves even
                for an unbound agent — the page itself shows the connect prompt. */}
            <Route path="knowledge/wix" element={<KnowledgeProviderPage provider="wix" />} />
            <Route path="knowledge/shopify" element={<KnowledgeProviderPage provider="shopify" />} />
            <Route path="knowledge/google-drive" element={<KnowledgePage section="googledrive" />} />
            <Route path="knowledge/onedrive" element={<KnowledgePage section="onedrive" />} />
            <Route path="knowledge/notion" element={<KnowledgePage section="notion" />} />

            {/* Notifications */}
            <Route path="notifications" element={<NotificationsPage />} />

            {/* Help — a sidebar leaf, and also the target of the header's
                question-mark button and the sidebar footer link. */}
            <Route path="help" element={<HelpPage />} />

            {/* Activity */}
            <Route path="activity/insights" element={<InsightsPage />} />
            <Route path="activity/inbox" element={<InboxPage />} />
            <Route
              path="activity/leads"
              element={<LeadsPage setUnreadLeadsCount={setUnreadLeadsCount} />}
            />
            <Route path="activity/reporting" element={<ReportingPage />} />

            {/* Deploy — Channels keeps the existing Integration page (with
                its internal tabs) for now; Design is the widget editor. */}
            <Route path="deploy/channels" element={<ChannelsPage />} />
            <Route path="deploy/design" element={<DesignPage />} />

            {/* Utility routes (reachable, not on the agent-level sidebar):
                bot management, account, billing, resources. */}
            <Route
              path="my-bots"
              element={
                <BotsOverview
                  onSettingsClick={handleSettingsClick}
                  onAnalyticsClick={handleAnalyticsClick}
                />
              }
            />
            <Route
              path="my-account"
              element={
                <UserSettings
                  logoutUser={() => logout()}
                  onSave={handleLanguageChangeFromAdmin}
                />
              }
            />
            <Route path="referral" element={<PartnerProgram />} />
            <Route path="feedback" element={<Feedback />} />
            <Route path="billing" element={<Billing />} />
            <Route path="billing/:botId" element={<BotBilling />} />

            {/* Account-level pages: workspaces + team members. These share the
                OVERVIEW_NAV sidebar (alongside Agents) rather than living in
                an agent's nav. */}
            <Route path="workspaces" element={<Team section="workspaces" />} />
            <Route path="team-members" element={<Team section="members" />} />
            <Route path="team" element={<Navigate to="/workspaces" replace />} />

            {/* Backward-compat redirects from the old IA. Deep links,
                bookmarks and a handful of in-app jumps still target these
                (carrying ?botId / ?bot_id / ?tab), so map them onto the
                new routes and forward the selected bot. */}
            <Route path="settings" element={<SettingsRedirect />} />
            <Route path="analytics" element={<AnalyticsRedirect />} />
            <Route path="integration" element={<IntegrationRedirect />} />
            <Route path="connect" element={<Navigate to="/deploy/channels" replace />} />
            <Route path="social-media" element={<Navigate to="/deploy/channels" replace />} />
            <Route path="data-connect" element={<Navigate to="/deploy/channels" replace />} />
            <Route path="integrations" element={<Navigate to="/deploy/channels" replace />} />
          </Route>

          <Route
            path="*"
            element={
              isAuthenticated ? (
                <Navigate to="/overview" replace />
              ) : (
                <LoginRedirect />
              )
            }
          />
        </Routes>
      </div>
    </>
  );
}

function App() {
  return (
    <Router>
      <MetaTracking />
      <UnsavedChangesProvider>
        <AppShell />
        <ErrorModal />
        <PermissionToast />
      </UnsavedChangesProvider>
    </Router>
  );
}

export default App;
